<template>
  <div class="card shadow-sm mt-3">
    <div class="card-header text-center py-1">
      <fa-icon v-if="icon" :icon="icon" /> {{ header }}
    </div>
    <div class="card-body pt-0">
      <div id="chart-wrapper">
        <div class="chart-container" :style="{ width: width + 'px', height: height + 'px' }">
          <canvas :id="chartId" :width="width" :height="height"></canvas>
          <div v-if="addonLabels.value" id="value" class="chart-addon" :style="css">
            <span v-if="valueLabel !== '?'">{{ valueLabel }}</span>
            <span v-else class="text-dark"><fa-icon icon="times"/></span>
          </div>
          <div v-if="addonLabels.min" id="min" class="chart-addon" :style="{ right: width + 'px' }">{{ min }}</div>
          <div v-if="addonLabels.max" id="max" class="chart-addon" :style="{ left: width + 'px' }">{{ max }}</div>
          <div v-if="addonLabels.unit" id="unit" class="chart-addon">[ {{ unit }} ]</div>
          <div v-if="marker" id="zero-marker" class="chart-addon"><fa-icon icon="sort-down"/></div>
        </div>
      </div>
    </div>
    <!--  -->
  </div>
</template>

<script>
import Chart from 'chart.js'
import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  name: 'gauge',
  props: {
    height: {
      type: Number,
      default: 150
    },
    icon: {
      type: String
    },
    marker: {
      type: Boolean,
      default: false
    },
    id: {
      type: String
    },
    header: {
      type: String,
      default: 'Gauge'
    },
    unit: {
      type: String,
      default: '%'
    },
    value: {
      type: Number
    },
    valueFormatter: {
      type: Function,
      default: (value) => {
        return Math.round(value)
      }
    },
    status: {
      type: Number
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    angle: {
      type: Number,
      default: 210
    },
    radius: {
      type: Number,
      default: 0.25
    },
    color: {
      type: String,
      default: '#8ffbaf'
    },
    checks: {
      type: Array,
      default: () => { return [] }
    },
    labels: {
      type: Object
    },
    css: {
      type: Object,
      default: () => { return { fontSize: '4rem', bottom: '2%' } }
    }
  },
  data () {
    return {
      chart: null,
      chartType: 'doughnut',
      borderWidth: [0, 0, 0],
      valueLabel: null,
      activeColor: null,
      checkStatus: null,
      defaultLabels: {
        min: true,
        max: true,
        unit: true,
        value: true
      }
    }
  },
  computed: {
    ...mapGetters(['colors']),
    chartId () {
      return 'chart-' + this.id
    },
    modalId () {
      return 'modal-' + this.id
    },
    width () {
      return this.height / 150 * 200
    },
    rotation () {
      return -0.5 * this.angle - 90
    },
    cutoutPercentage () {
      let p = Math.round(this.radius * 100)
      p = Math.max(0, p)
      p = Math.min(p, 100)

      return 100 - p
    },
    addonLabels () {
      return _.assign(this.defaultLabels, this.labels)
    }
  },
  watch: {
    value: function () {
      this.runChecks()
      this.runValueFormatter()
      this.updateCharts()
    }
  },
  methods: {
    runValueFormatter () {
      if (isNaN(this.value)) {
        this.valueLabel = '?'
      } else if (this.value === null) {
        this.valueLabel = '_'
      } else {
        this.valueLabel = this.valueFormatter(this.value, this.status, this.checkStatus)
      }
    },
    runChecks () {
      if (isNaN(this.value)) return
      if (this.value === null) return

      this.activeColor = this.color
      this.checkStatus = null

      this.checks.forEach((check, index) => {
        if (check.check(this.value)) {
          this.activeColor = check.color
          this.checkStatus = check.status
        }
      })
    },
    getChartColor () {
      return [
        this.colors.light,
        this.activeColor,
        this.colors.light
      ]
    },
    updateCharts () {
      this.chart.data.datasets[0].data = this.getChartData()
      this.chart.data.datasets[0].backgroundColor = this.getChartColor()
      this.chart.update()
    },
    setBackgroundColor (color) {
      this.backgroundColor[1] = color
    },
    getChartData () {
      let value = this.value
      if (isNaN(value)) value = null

      value = Math.max(value, this.min)
      value = Math.min(value, this.max)

      let data = []
      if (value < 0) {
        if (this.max < 0) {
          data[0] = Math.abs(this.min) - Math.abs(value)
          data[1] = Math.abs(value) - Math.abs(this.max)
          data[2] = 0
        } else {
          data[0] = Math.abs(this.min) - Math.abs(value)
          data[1] = Math.abs(value)
          data[2] = Math.abs(this.max)
        }
      } else {
        if (this.min > 0) {
          data[0] = 0
          data[1] = Math.abs(value) - Math.abs(this.min)
          data[2] = Math.abs(this.max) - Math.abs(value)
        } else {
          data[0] = Math.abs(this.min)
          data[1] = Math.abs(value)
          data[2] = Math.abs(this.max) - Math.abs(value)
        }
      }

      return data
    },
    updateChartData () {
      this.chart.data.datasets[0].data = this.getChartData()
    },
    updateChartColor () {
      this.chart.data.datasets[0].backgroundColor = this.backgroundColor
    },
    createChart () {
      const ctx = document.getElementById(this.chartId).getContext('2d')
      this.chart = new Chart(ctx, {
        type: this.chartType,
        data: {
          datasets: [
            {
              data: this.getChartData(),
              backgroundColor: this.getChartColor(),
              borderWidth: this.borderWidth
            }
          ]
        },
        options: {
          // animation: {
          //   duration: 1000
          // },
          maintainAspectRatio: true,
          responsive: true,
          events: null,
          cutoutPercentage: this.cutoutPercentage,
          circumference: this.angle / 180 * Math.PI,
          rotation: this.rotation / 180 * Math.PI
        }
      })
    }
  },
  components: {
  },
  mounted () {
    this.runChecks()
    this.runValueFormatter()
    this.createChart()
  }
}
</script>

<style lang="scss" scoped>
.chart-container {
  position: relative;
  margin: auto;
  cursor: pointer;
}

.chart-addon {
  display: inline-block;
  position: absolute;
  padding-bottom: inherit;
}

#value {
  font-size: 4.0rem;
  font-weight: 300;
  left: -30%;
  right: -30%;
  text-align: center;
}

#min {
  bottom: -1rem;
  text-align: right;
}

#max {
  bottom: -1rem;
  text-align: left;
}

#unit {
  bottom: -5px;
  left: 0;
  right: 0;
  text-align: center;
}

#zero-marker {
  top: 0;
  left: 0;
  right: 0;
  text-align: center;
}

.card-header {
  border-bottom: none;  
}

.card-body {
  padding-top: 10px;
}

.my-modal {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.1);
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000;
}

.my-modal-content {
  z-index: 3000;
}
</style>



// WEBPACK FOOTER //
// src/components/charts/Gauge.vue